import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import { supabase } from '@/lib/supabase'

const AuthContext = createContext(null)

// =============================================================================
// AuthProvider — rewritten state machine (review item §2.2)
// =============================================================================
// Fixes from the previous version:
//   * No duplicate profile fetch on load. onAuthStateChange fires an
//     INITIAL_SESSION event immediately with the restored session, so the
//     separate getSession() call (which caused a second fetch) is gone.
//   * `loading` has a single source of truth — no racing 5s/6s timeouts that
//     could bounce a logged-in user to /auth or leave a page spinning forever.
//   * The onAuthStateChange callback only sets React state; it never awaits a
//     Supabase call (doing so inside the callback can deadlock the auth client).
//     Profile loading happens in a separate effect keyed on the user id.
//   * All callbacks are memoised, so consumers' effects that depend on
//     refreshProfile / updateProfile don't re-run on every render.
// =============================================================================

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch a profile row, bounded by a 10s timeout so a hung request can't keep
  // the app in a permanent loading state. Stable identity (no deps).
  const fetchProfile = useCallback(async (userId) => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10000)
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .abortSignal(controller.signal)
        .single()
      if (error) {
        console.error('Profile fetch error:', error)
        return null
      }
      return data
    } catch (err) {
      console.error('Profile fetch error:', err)
      return null
    } finally {
      clearTimeout(timeout)
    }
  }, [])

  // 1. Single auth subscription. INITIAL_SESSION fires right after subscribe
  //    with the restored session (or null). All loading-state transitions that
  //    aren't tied to the profile fetch happen here in the callback — which is
  //    React's sanctioned place for "setState in response to an external
  //    system", and keeps effect 2 free of synchronous setState.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession)
        if (!newSession) {
          // Signed out: nothing left to load.
          setProfile(null)
          setLoading(false)
        } else if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          // A profile fetch will follow in effect 2. TOKEN_REFRESHED /
          // USER_UPDATED keep the same user, so effect 2 won't refetch and we
          // intentionally leave `loading` untouched.
          setLoading(true)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [])

  // 2. Load the profile whenever the signed-in user changes. Keyed on the user
  //    id, so a token refresh (same user, new session object) doesn't refetch.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) return
    let cancelled = false
    fetchProfile(userId).then((data) => {
      if (cancelled) return
      setProfile(data)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [session?.user?.id, fetchProfile])

  // Manual refresh (after an action that changed the profile). Keeps the
  // existing profile on a failed fetch rather than blanking it.
  const refreshProfile = useCallback(async () => {
    const userId = session?.user?.id
    if (!userId) return
    const data = await fetchProfile(userId)
    if (data) setProfile(data)
  }, [session?.user?.id, fetchProfile])

  // Updates only NON-privileged columns (full_name, bio, avatars, consent…).
  // points / CO2 / is_pro are now rejected by a DB trigger and must go through
  // the server-side RPCs / Stripe webhook (see migration 0001).
  const updateProfile = useCallback(async (updates) => {
    const userId = session?.user?.id
    if (!userId) return { error: 'Not logged in' }
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    if (!error && data) setProfile(data)
    return { data, error }
  }, [session?.user?.id])

  const signInWithEmail = useCallback(
    (email, password) => supabase.auth.signInWithPassword({ email, password }),
    [],
  )

  const signUpWithEmail = useCallback(
    (email, password, fullName) =>
      supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      }),
    [],
  )

  const signInWithGoogle = useCallback(
    () => supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    }),
    [],
  )

  const signInWithApple = useCallback(
    () => supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    }),
    [],
  )

  const signOut = useCallback(() => supabase.auth.signOut(), [])

  const value = useMemo(() => ({
    session,
    profile,
    loading,
    user: session?.user ?? null,
    isAuthenticated: !!session,
    refreshProfile,
    updateProfile,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithApple,
    signOut,
  }), [
    session, profile, loading,
    refreshProfile, updateProfile,
    signInWithEmail, signUpWithEmail,
    signInWithGoogle, signInWithApple, signOut,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook is conventionally colocated with its provider
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
